import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from '@/shared/utils/cn';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[10px] text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring/20 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: 
          "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-[0_4px_15px_rgba(46,134,171,0.3)] active:scale-[0.98]",
        
        glass: 
          "bg-card/40 backdrop-blur-xl border border-border/50 hover:bg-card/60 hover:border-primary/30",
        
        neon: 
          "bg-gradient-to-r from-primary to-[#00D4AA] text-white hover:shadow-[0_6px_20px_rgba(46,134,171,0.35)] active:scale-[0.98]",
        
        destructive: 
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-[0_4px_15px_rgba(192,57,43,0.25)]",
        
        outline: 
          "border border-border bg-transparent hover:bg-muted hover:border-primary/30 active:scale-[0.98]",
        
        secondary: 
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:scale-[0.98]",
        
        ghost: 
          "hover:bg-muted hover:text-foreground active:scale-[0.98]",
        
        link: 
          "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-[8px] px-3 text-xs",
        lg: "h-11 rounded-[12px] px-8 text-base",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
